import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { AreaChart } from "@mantine/charts";
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Center,
  ComboboxItem,
  Divider,
  Grid,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
  IconCalendarDollar,
  IconCashRegister,
  IconCheck,
  IconCloudDownload,
  IconExternalLink,
  IconEye,
  IconInfoCircle,
  IconInfoTriangle,
  IconLoader2,
  IconMoneybagMove,
  IconMoneybagMoveBack,
  IconRefresh,
  IconSearch,
  IconX,
} from "@tabler/icons-react";

import { WithDashboardNavbar } from "@layouts/WithDashboardNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { CommissionDetails } from "@components/CommissionDetails";
import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
} from "@components/Table";
import { TabsContent, TabsList, TabsRoot } from "@components/Tabs";

import { formatDateToString } from "@utils/dateValidation";
import { getCustomDateRange } from "@utils/getCustomDateRange";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";

import { DEFAULT_PAGINATION, ROLES } from "@constants/index";

import { useApproveAndPayPayout } from "@hooks/mutations/payouts/useApproveAndPayPayout";
import { useRetryPayout } from "@hooks/mutations/payouts/useRetryPayout";
import { useSyncPayoutStatus } from "@hooks/mutations/payouts/useSyncPayoutStatus";
import { useGetEarningsDailyTrend } from "@hooks/queries/dashboard/useGetEaningsDailyTrend";
import { useGetEarningsByServices } from "@hooks/queries/dashboard/useGetEarningsByServices";
import { useGetEarningsByStaff } from "@hooks/queries/dashboard/useGetEarningsByStaff";
import { useGetPayoutIssuePreview } from "@hooks/queries/dashboard/useGetPayoutIssuePreview";
import { useGetPayouts } from "@hooks/queries/dashboard/useGetPayouts";
import { useGetPayoutSummary } from "@hooks/queries/dashboard/useGetPayoutSummary";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";
import { useTableColumnCount } from "@hooks/useTableColumnCount";

import { DateRangeSelect } from "../components/DateRangeSelect";
import { EarningsOverviewCard } from "../components/EarningsOverviewCard";

import { CommissionReconciliationModal } from "./components/CommissionReconciliationModal";
import { IssuePayoutModal } from "./components/IssuePayoutModal";
import { ReferralRewardsTab } from "./components/ReferralRewardsTab";


const EarningsOverview = () => {
  const { t, i18n } = useTranslation();
  const { mutate: approveAndPay, isLoading: isApproving } = useApproveAndPayPayout();
  const { mutate: retryPayout, isLoading: isRetrying } = useRetryPayout();
  const { syncPayoutStatus, isSyncing } = useSyncPayoutStatus();

  const [dateRange, setDateRange] = useState<string | null>("last-7-days");
  const [customDateRange, setCustomDateRange] = useState<
    [Date | null, Date | null]
  >([null, null]);

  const [payoutModalOpened, { open: openPayoutModal, close: closePayoutModal }] = useDisclosure(false);
  const [
    reconciliationModalOpened,
    { open: openReconciliationModal, close: closeReconciliationModal },
  ] = useDisclosure(false);

  // Earnings by Staff states
  const [earningsByStaffDateRange, setEarningsByStaffDateRange] = useState<
    string | null
  >("last-7-days");
  const [customEarningsByStaffDateRange, setCustomEarningsByStaffDateRange] =
    useState<[Date | null, Date | null]>([null, null]);
  const [earningsByStaffCurrentPage, setEarningsByStaffCurrentPage] =
    useState(1);
  const [searchForStaff, setSearchForStaff] = useState("");
  const [debouncedSearchForStaff] = useDebouncedValue(searchForStaff, 300);
  const {
    columnCount: earningsByStaffColumnCount,
    tableRef: earningsByStaffTableRef,
  } = useTableColumnCount();

  // Earnings by Services states
  const [earningsByServicesDateRange, setEarningsByServicesDateRange] =
    useState<string | null>("last-7-days");
  const [
    customEarningsByServicesDateRange,
    setCustomEarningsByServicesDateRange,
  ] = useState<[Date | null, Date | null]>([null, null]);
  const [earningsByServicesCurrentPage, setEarningsByServicesCurrentPage] =
    useState(1);
  const {
    columnCount: earningsByServicesColumnCount,
    tableRef: earningsByServicesTableRef,
  } = useTableColumnCount();
  const [searchForCid, setSearchForCid] = useState("");
  const [debouncedSearchForCid] = useDebouncedValue(searchForCid, 300);
  const [billStatus, setBillStatus] = useState<ComboboxItem | null>({
    label: t("all"),
    value: "all",
  });
  const [reportStatus, setReportStatus] = useState<ComboboxItem | null>({
    label: t("all"),
    value: "all",
  });
  const [payoutStatus, setPayoutStatus] = useState<ComboboxItem | null>({
    label: t("all"),
    value: "all",
  });

  const [doctor, setDoctor] = useState<string | null>(null);

  const { data: doctorsData, isLoading: isDoctorsDataLoading } = useGetUsers({
    role: "doctor",
  });

  // Payouts state
  const [payoutsCurrentPage, setPayoutsCurrentPage] = useState(1);
  const [payoutsStatusFilter, setPayoutsStatusFilter] = useState<ComboboxItem | null>({
    label: t("all"),
    value: "all",
  });
  const {
    columnCount: payoutsColumnCount,
    tableRef: payoutsTableRef,
  } = useTableColumnCount();

  const {
    data: payoutsData,
    isLoading: isPayoutsLoading,
    isError: isPayoutsError,
    isSuccess: isPayoutsSuccess,
  } = useGetPayouts({
    status: payoutsStatusFilter?.value !== "all" ? payoutsStatusFilter?.value : undefined,
    pagination: {
      page: payoutsCurrentPage,
      pageSize: DEFAULT_PAGINATION.pageSize,
    },
  });
  const { data: payoutSummary, isLoading: isPayoutSummaryLoading } =
    useGetPayoutSummary({
      dateRange: dateRange as string,
      ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
    });
  const {
    data: earningsDailyTrendData,
    isLoading: isEarningsDailyTrendLoading,
    isError: isEarningsDailyTrendError,
    isSuccess: isEarningsDailyTrendSuccess,
  } = useGetEarningsDailyTrend({
    dateRange: dateRange as string,
    ...(dateRange === "custom" && getCustomDateRange(customDateRange)),
  });
  const {
    data: earningsByStaffData,
    isLoading: isEarningsByStaffLoading,
    isError: isEarningsByStaffError,
    isSuccess: isEarningsByStaffSuccess,
  } = useGetEarningsByStaff({
    dateRange: earningsByStaffDateRange as string,
    ...(earningsByStaffDateRange === "custom" &&
      getCustomDateRange(customEarningsByStaffDateRange)),
    pagination: {
      page: earningsByStaffCurrentPage,
      pageSize: DEFAULT_PAGINATION.pageSize,
    },
    search: debouncedSearchForStaff,
  });
  const {
    data: earningsByServicesData,
    isLoading: isEarningsByServicesLoading,
    isError: isEarningsByServicesError,
    isSuccess: isEarningsByServicesSuccess,
  } = useGetEarningsByServices({
    dateRange: earningsByServicesDateRange as string,
    ...(earningsByServicesDateRange === "custom" &&
      getCustomDateRange(customEarningsByServicesDateRange)),
    pagination: {
      page: earningsByServicesCurrentPage,
      pageSize: DEFAULT_PAGINATION.pageSize,
    },
    search: debouncedSearchForCid,
    billStatus: billStatus?.value !== "all" ? billStatus?.value : "",
    reportStatus: reportStatus?.value !== "all" ? reportStatus?.value : "",
    payoutStatus: payoutStatus?.value !== "all" ? payoutStatus?.value : "",
    userId: doctor ?? "",
  });

  const doctors = useMemo(() => {
    return doctorsData?.map(item => ({
      label: item.fullName,
      value: item.id || (item as any)._id,
    }));
  }, [doctorsData]);

  const issuePayoutCustomRange: { startDate?: string; endDate?: string } =
    earningsByServicesDateRange === "custom"
      ? getCustomDateRange(customEarningsByServicesDateRange) || {}
      : {};
  const {
    data: payoutIssuePreview,
    isLoading: isPayoutIssuePreviewLoading,
    isError: isPayoutIssuePreviewError,
  } = useGetPayoutIssuePreview({
    recipientId: doctor,
    dateRange: earningsByServicesDateRange as string,
    ...issuePayoutCustomRange,
  });

  const earningsByStaffRows = useMemo(() => {
    return earningsByStaffData?.newData?.map(item => (
      <Table.Tr key={item.id || (item as any)._id}>
        <Table.Td>
          <Stack gap={5}>
            <Text fz={14} c="black">
              {item.name}
            </Text>
            <Text c="dimmed" fz={13}>
              {item.role}
            </Text>
          </Stack>
        </Table.Td>
        <Table.Td>CFA {item.totalEarnings}</Table.Td>
        <Table.Td c={item?.currentCommissionPercentage ? "black" : "dimmed"}>
          {item?.currentCommissionPercentage
            ? item.currentCommissionPercentage + "%"
            : "N/A"}
        </Table.Td>
        <Table.Td c={item?.nextCommissionPercentage ? "black" : "dimmed"}>
          {item?.nextCommissionPercentage
            ? item.nextCommissionPercentage + "%"
            : "N/A"}
        </Table.Td>
        <Table.Td c={item?.thresholdForNextCommission ? "black" : "dimmed"}>
          {item?.thresholdForNextCommission
            ? item.thresholdForNextCommission + "%"
            : "N/A"}
        </Table.Td>
      </Table.Tr>
    ));
  }, [earningsByStaffData, i18n.language]);

  const earningsByServicesRows = useMemo(() => {
    return earningsByServicesData?.newData?.map(item => (
      <Table.Tr key={item.paymentId}>
        <Table.Td>
          <Stack gap={5}>
            <Text fz={14} c="black">
              {formatDateToString(item.dateOfService as Date)}
            </Text>
            <Text c="dimmed" fz={13}>
              {item.dateOfService?.toLocaleTimeString(i18n.language, {
                timeStyle: "short",
              })}
            </Text>
          </Stack>
        </Table.Td>
        <Table.Td>
          {item.consultationId ? (
            <Anchor
              component={Link}
              to={`/patients/${item.patientId}/consultation/general/${item.consultationId}`}
              ff={"var(--font-secondary)"}
              fz={14}
              c="accent"
              fw={500}>
              {item.cId}
            </Anchor>
          ) : (
            <Text fz={14}>{item.cId}</Text>
          )}
        </Table.Td>
        <Table.Td tt="capitalize">{item.serviceName.replaceAll("_", " ")}</Table.Td>
        <Table.Td>
          <Group gap={5} align="center">
            <Badge
              w={"max-content"}
              fz={12}
              c={item.billStatus === "pending" ? "yellow.8" : "green.8"}
              bg={item.billStatus === "pending" ? "yellow.0" : "green.0"}>
              {item.billStatus}
            </Badge>

            {item.consultationId && (
              <Link
                to={`/patients/${item.patientId}/billing/${item.consultationId}`}
                style={{ lineHeight: 0 }}>
                <IconExternalLink size={16} stroke={1.5} />
              </Link>
            )}
          </Group>
        </Table.Td>
        <Table.Td>{item.staffName}</Table.Td>
        <Table.Td>
          <Badge
            w={"max-content"}
            fz={12}
            c={item.reportStatus === "incomplete" ? "red.8" : "green.8"}
            bg={item.reportStatus === "incomplete" ? "red.0" : "green.0"}>
            {item.reportStatus}
          </Badge>
        </Table.Td>
        <Table.Td>
          {Intl.NumberFormat(i18n.language, {
            style: "currency",
            currency: "CFA",
          }).format(item.amountPaidIn || 0)}
        </Table.Td>
        <Table.Td>
          <Group gap={5} align="center">
            {Intl.NumberFormat(i18n.language, {
              style: "currency",
              currency: "CFA",
            }).format(item.amountDue || 0)}
            <CommissionDetails
              percentage={item.commissionPercentage}
              source={item.commissionPercentageSource}
            />
            {item.payoutStatus === "complete" || item.payoutStatus === "completed" ? (
              <Tooltip
                events={{ hover: true, focus: true, touch: true }}
                label={t("complete")}
                withArrow>
                <IconCheck size={16} stroke={1.5} color="green" />
              </Tooltip>
            ) : null}
            {item.payoutStatus === "pending" ? (
              <Tooltip
                events={{ hover: true, focus: true, touch: true }}
                label={t("pending")}
                withArrow>
                <IconInfoTriangle size={16} stroke={2} color="orange" />
              </Tooltip>
            ) : null}
            {item.payoutStatus === "processing" ? (
              <Tooltip
                events={{ hover: true, focus: true, touch: true }}
                label={t("processing")}
                withArrow>
                <IconLoader2 size={16} stroke={2} color="blue" style={{ animation: "spin 2s linear infinite" }} />
              </Tooltip>
            ) : null}
            {item.payoutStatus === "failed" ? (
              <Tooltip
                events={{ hover: true, focus: true, touch: true }}
                label={t("failed")}
                withArrow>
                <IconX size={16} stroke={1.5} color="red" />
              </Tooltip>
            ) : null}
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [earningsByServicesData, i18n.language, t]);

  const payoutsRows = useMemo(() => {
    return payoutsData?.newData?.map(item => (
      <Table.Tr key={item.id || (item as any)._id}>
        <Table.Td>
          <Stack gap={5}>
            <Text fz={14} c="black">
              {formatDateToString(new Date(item.created_at))}
            </Text>
            <Text c="dimmed" fz={13}>
              {new Date(item.created_at).toLocaleTimeString(i18n.language, {
                timeStyle: "short",
              })}
            </Text>
          </Stack>
        </Table.Td>
        <Table.Td>
          <Stack gap={5}>
            <Text fz={14} c="black">
              {item.recipient_name}
            </Text>
            {item.recipient_email && (
              <Text c="dimmed" fz={13}>
                {item.recipient_email}
              </Text>
            )}
          </Stack>
        </Table.Td>
        <Table.Td>
          {Intl.NumberFormat(i18n.language, {
            style: "currency",
            currency: "CFA",
          }).format(item.amount || 0)}
        </Table.Td>
        <Table.Td>
          <Badge w={"max-content"} fz={12} variant="light" color="blue">
            {item.payout_type}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Badge w={"max-content"} fz={12} variant="light" color="gray">
            {item.payout_method}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Group gap={5} align="center">
            {item.status === "completed" ? (
              <Badge w={"max-content"} fz={12} c="green.8" bg="green.0">
                {t("completed")}
              </Badge>
            ) : item.status === "pending" || item.status === "requested" ? (
              <Badge w={"max-content"} fz={12} c="orange.8" bg="orange.0">
                {t(item.status)}
              </Badge>
            ) : item.status === "processing" ? (
              <Badge w={"max-content"} fz={12} c="blue.8" bg="blue.0">
                {t("processing")}
              </Badge>
            ) : (
              <Badge w={"max-content"} fz={12} c="red.8" bg="red.0">
                {item.status}
              </Badge>
            )}
            {item.status === "failed" && item.failure_reason && (
              <Tooltip label={item.failure_reason} withArrow>
                <IconInfoCircle size={16} color="red" />
              </Tooltip>
            )}
          </Group>
        </Table.Td>
        <Table.Td align="right">
          <Group gap={8} justify="flex-end" wrap="nowrap">
            {item.payout_type === "referral_reward" && (
              <Tooltip label={t("referral-rewards.title")}>
                <ActionIcon component={Link} to="?tab=referral-rewards" variant="light">
                  <IconEye size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            {item.status === "processing" && item.joonapay_group_uuid && (
              <Tooltip label={t("sync-payout-status", "Sync Status with JoonaPay")} withArrow position="top-end">
                <ActionIcon
                  variant="light"
                  color="blue"
                  disabled={isSyncing}
                  loading={isSyncing}
                  onClick={() => syncPayoutStatus(item.id || (item as any)._id)}
                >
                  <IconCloudDownload size={16} />
                </ActionIcon>
              </Tooltip>
            )}

            {item.status === "failed" && (
              <Tooltip label={t("retry-payout", "Retry Payout")} withArrow position="top-end">
                <ActionIcon
                  variant="light"
                  color="blue"
                  disabled={isRetrying}
                  loading={isRetrying}
                  onClick={() => {
                    modals.openConfirmModal({
                      title: t("retry-payout-title", "Retry Payout"),
                      children: (
                        <Text size="sm">
                          {t("retry-payout-confirmation", "Are you sure you want to retry this failed payout?")}
                        </Text>
                      ),
                      labels: { confirm: t("confirm", "Confirm"), cancel: t("cancel", "Cancel") },
                      confirmProps: { color: "blue" },
                      onConfirm: () => retryPayout(item.id || (item as any)._id),
                    });
                  }}
                >
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
            )}

            {(item.status === "requested" || item.status === "pending") && item.payout_method === "mobile_money" && (
              <Tooltip
                label={t("missing-mobile-money", "Cannot process automated payout: Doctor is missing Mobile Money configuration.")}
                disabled={!!item.mobile_money_number}
                withArrow
                position="top-end"
              >
                <Box style={{ display: 'inline-block' }}>
                  <Button
                    size="xs"
                    variant="light"
                    disabled={!item.mobile_money_number || isApproving}
                    loading={isApproving}
                    onClick={() => {
                      modals.openConfirmModal({
                        title: t("approve-payout-title", "Approve Payout"),
                        children: (
                          <Text size="sm">
                            {t("approve-payout-confirmation", "Are you sure you want to approve and process this payout?")}
                          </Text>
                        ),
                        labels: { confirm: t("confirm", "Confirm"), cancel: t("cancel", "Cancel") },
                        confirmProps: { color: "blue" },
                        onConfirm: () => approveAndPay(item.id || (item as any)._id),
                      });
                    }}
                  >
                    {t("approve-pay", "Approve & Pay")}
                  </Button>
                </Box>
              </Tooltip>
            )}
          </Group>
        </Table.Td>
      </Table.Tr>
    ));
  }, [payoutsData, i18n.language, isApproving, approveAndPay, isRetrying, retryPayout, isSyncing, syncPayoutStatus, t]);

  return (
    <>
        <IssuePayoutModal 
          opened={payoutModalOpened} 
          onClose={closePayoutModal}
          staffId={doctor}
          staffName={doctors?.find(d => d.value === doctor)?.label || null}
          amount={payoutIssuePreview?.amount || 0}
          paymentCount={payoutIssuePreview?.payment_count || 0}
          dateRange={earningsByServicesDateRange as string}
          startDate={issuePayoutCustomRange.startDate}
          endDate={issuePayoutCustomRange.endDate}
        />
        <CommissionReconciliationModal
          opened={reconciliationModalOpened}
          onClose={closeReconciliationModal}
          staffId={doctor}
          staffName={doctors?.find(d => d.value === doctor)?.label || null}
        />
        <Group mt={"1rem"} justify="start">
          <Text fz={15}>{t("displaying-stats-for")}</Text>
          <DateRangeSelect
            dateRange={dateRange}
            setDateRange={setDateRange}
            customDateRange={customDateRange}
            setCustomDateRange={setCustomDateRange}
          />
        </Group>
        <SimpleGrid cols={{ base: 2, md: 3, lg1: 3, lg2: 4 }} mt={"2rem"}>
          <EarningsOverviewCard
            title={t("total-amount-paid-in")}
            amount={payoutSummary?.totalPaidIn?.amount}
            numOfServices={payoutSummary?.totalPaidIn?.services}
            isLoading={isPayoutSummaryLoading}
            amountPostfix="CFA"
            icon={
              <Group
                bg="accent.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconMoneybagMoveBack
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-accent-6)"
                />
              </Group>
            }
          />
          <EarningsOverviewCard
            title={t("outstanding-pay-ins")}
            amount={payoutSummary?.outstandingPayIns?.amount}
            numOfServices={payoutSummary?.outstandingPayIns?.services}
            isLoading={!payoutSummary}
            amountPostfix="CFA"
            icon={
              <Group
                bg="yellow.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconCashRegister
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-yellow-6)"
                />
              </Group>
            }
          />
          <EarningsOverviewCard
            title={t("total-amount-paid-out")}
            amount={payoutSummary?.totalPaidOut?.amount}
            numOfServices={payoutSummary?.totalPaidOut?.services}
            isLoading={isPayoutSummaryLoading}
            amountPostfix="CFA"
            icon={
              <Group
                bg="green.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconMoneybagMove
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-green-6)"
                />
              </Group>
            }
          />
          <EarningsOverviewCard
            title={t("outstanding-payouts")}
            amount={payoutSummary?.outstandingPayOuts?.amount}
            numOfServices={payoutSummary?.outstandingPayOuts?.services}
            isLoading={isPayoutSummaryLoading}
            amountPostfix="CFA"
            icon={
              <Group
                bg="orange.0"
                w={30}
                h={30}
                justify="center"
                style={{ borderRadius: "var(--border-radius-1)" }}>
                <IconCalendarDollar
                  size={20}
                  stroke={1.5}
                  color="var(--mantine-color-orange-6)"
                />
              </Group>
            }
          />
        </SimpleGrid>

        <Grid mt="3rem" gutter={"lg"}>
          <Grid.Col span={{ base: 12 }}>
            <Paper withBorder p="md">
              <Group justify="space-between">
                <Title order={3} fz={17} fw={500}>
                  {t("earnings-over-time")}
                </Title>
              </Group>

              {renderLoadingErrorData({
                isLoading: isEarningsDailyTrendLoading,
                isError: isEarningsDailyTrendError,
                isSuccess: isEarningsDailyTrendSuccess,
                dataLength: earningsDailyTrendData?.length ?? 0,
                emptyDataComponent: (
                  <Center mt={"1rem"} h={300}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("empty-data")}
                    </Text>
                  </Center>
                ),
                errorComponent: (
                  <Center mt={"1rem"} h={300}>
                    <Text
                      fz={15}
                      c="dimmed"
                      lh={1.5}
                      ta={"center"}
                      maw={"45ch"}>
                      {t("encountered-error")}
                    </Text>
                  </Center>
                ),
                loadingComponent: (
                  <Skeleton h={300} w={"100%"} mt={"1rem"} />
                  // <Group h={300} mt={"1rem"} align="end" justify="space-evenly">
                  // Loading...
                  // </Group>
                ),
                dataComponent: (
                  <AreaChart
                    mt={"1rem"}
                    h={300}
                    data={earningsDailyTrendData || []}
                    dataKey="date"
                    withLegend
                    tooltipAnimationDuration={150}
                    withTooltip
                    legendProps={{ verticalAlign: "bottom" }}
                    series={[
                      { name: "amountPaidIn", color: "blue.5" },
                      { name: "outstandingPayIns", color: "yellow.5" },
                      { name: "amountPaidOUt", color: "green.5" },
                      { name: "outstandingPayOuts", color: "red.5" },
                    ]}
                    tickLine="y"
                    tt={"capitalize"}
                  />
                ),
              })}
            </Paper>
          </Grid.Col>
        </Grid>

        <Grid mt="3rem" gutter={"lg"}>
          <Grid.Col span={{ base: 12 }}>
            <Paper withBorder p="md" w={{ base: "fit-content", md: "100%" }}>
              <Group justify="space-between">
                <Title order={3} fz={17} fw={500}>
                  {t("earnings-by-services")}
                </Title>
                <Group gap={"xs"}>
                  {/* <Button leftSection={<IconDownload size={16} />} disabled>
                    {t("export-csv")}
                  </Button> */}
                  <DateRangeSelect
                    dateRange={earningsByServicesDateRange}
                    setDateRange={newRange => {
                      setEarningsByServicesDateRange(newRange);
                      setEarningsByServicesCurrentPage(1); // Reset to first page when date range changes
                    }}
                    customDateRange={customEarningsByServicesDateRange}
                    setCustomDateRange={newRange => {
                      setCustomEarningsByServicesDateRange(newRange);
                    }}
                  />
                </Group>
              </Group>
              <Group mt="1rem">
                <TextInput
                  placeholder={t("search-for-consultation")}
                  miw={300}
                  value={searchForCid}
                  onChange={e => {
                    setSearchForCid(e.target.value);
                    setEarningsByServicesCurrentPage(1);
                  }}
                />
                <Group>
                  <Text fz={14} fw={500}>
                    {t("bill-status")}:
                  </Text>
                  <Select
                    maw={150}
                    data={[
                      { label: t("all"), value: "all" },
                      { label: t("paid"), value: "paid" },
                      { label: t("pending"), value: "pending" },
                    ]}
                    value={billStatus?.value ?? null}
                    onChange={(_, option) => {
                      setBillStatus(option);
                      setEarningsByServicesCurrentPage(1);
                    }}
                    clearable
                  />
                </Group>
                <Group>
                  <Text fz={14} fw={500}>
                    {t("report-status")}:
                  </Text>
                  <Select
                    maw={150}
                    data={[
                      { label: t("all"), value: "all" },
                      { label: t("complete"), value: "complete" },
                      { label: t("incomplete"), value: "incomplete" },
                    ]}
                    value={reportStatus?.value ?? null}
                    onChange={(_, option) => {
                      setReportStatus(option);
                      setEarningsByServicesCurrentPage(1);
                    }}
                    clearable
                  />
                </Group>
                <Group>
                  <Text fz={14} fw={500}>
                    {t("payout-status")}:
                  </Text>
                  <Select
                    maw={150}
                    data={[
                      { label: t("all"), value: "all" },
                      { label: t("complete"), value: "complete" },
                      { label: t("pending"), value: "pending" },
                      { label: t("processing"), value: "processing" },
                      { label: t("failed"), value: "failed" },
                    ]}
                    value={payoutStatus?.value ?? null}
                    onChange={(_, option) => {
                      setPayoutStatus(option);
                      setEarningsByServicesCurrentPage(1);
                    }}
                    clearable
                  />
                </Group>
              </Group>

              <Divider my="md" />
              
              <Group justify="space-between" align="flex-end" mb="md">
                <Group gap="xl">
                  <Stack gap={0}>
                    <Text fz={13} c="dimmed">{t("total-amount-earned")}</Text>
                    <Text fz={18} fw={600}>
                      {Intl.NumberFormat(i18n.language, {
                        style: "currency",
                        currency: "CFA",
                      }).format(
                        earningsByServicesData?.totals?.totalAmountDueToBePaidOut || 0
                      )}
                    </Text>
                  </Stack>
                  <Stack gap={0}>
                    <Text fz={13} c="dimmed">{t("total-amount-paid-out")}</Text>
                    <Text fz={18} fw={600} c="green.7">
                      {Intl.NumberFormat(i18n.language, {
                        style: "currency",
                        currency: "CFA",
                      }).format(earningsByServicesData?.totals?.totalAmountPaidOut || 0)}
                    </Text>
                  </Stack>
                  <Stack gap={0}>
                    <Text fz={13} c="dimmed">{t("processing-payout")}</Text>
                    <Text fz={18} fw={600} c="blue.7">
                      {Intl.NumberFormat(i18n.language, {
                        style: "currency",
                        currency: "CFA",
                      }).format(earningsByServicesData?.totals?.totalProcessingPayout || 0)}
                    </Text>
                  </Stack>
                  <Stack gap={0}>
                    <Text fz={13} c="dimmed">{t("outstanding-payout")}</Text>
                    <Text fz={18} fw={600} c="orange.7">
                      {Intl.NumberFormat(i18n.language, {
                        style: "currency",
                        currency: "CFA",
                      }).format(earningsByServicesData?.totals?.totalOutstandingPayout || 0)}
                    </Text>
                  </Stack>
                  <Stack gap={0}>
                    <Text fz={13} c="dimmed" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {t("pending-report-completion", "Pending Report")}
                      <Tooltip label={t("pending-report-tooltip", "Amount paid by patient but blocked until doctor completes the consultation report")} withArrow>
                        <IconInfoCircle size={14} />
                      </Tooltip>
                    </Text>
                    <Text fz={18} fw={600} c="red.7">
                      {Intl.NumberFormat(i18n.language, {
                        style: "currency",
                        currency: "CFA",
                      }).format(earningsByServicesData?.totals?.totalPendingReportCompletion || 0)}
                    </Text>
                  </Stack>
                </Group>
                
                <Group gap="xs">
                  <Tooltip
                    label={t("select-a-doctor-first")}
                    disabled={!!doctor}
                    withArrow>
                    <Box>
                      <Button
                        variant="outline"
                        leftSection={<IconRefresh size={16} />}
                        onClick={openReconciliationModal}
                        disabled={!doctor}>
                        {t("reconcile-commissions", "Reconcile commissions")}
                      </Button>
                    </Box>
                  </Tooltip>
                  <Tooltip
                    label={
                      !doctor
                        ? t("select-a-doctor-first")
                        : isPayoutIssuePreviewError
                          ? "Unable to calculate the eligible payout"
                          : t("no-outstanding-payout")
                    }
                    disabled={!!(payoutIssuePreview?.amount && doctor)}
                    withArrow>
                    <Box>
                      <Button
                        color="primary"
                        onClick={openPayoutModal}
                        loading={isPayoutIssuePreviewLoading}
                        disabled={!doctor || !payoutIssuePreview?.amount || isPayoutIssuePreviewError}>
                        {t("issue-payout")}
                      </Button>
                    </Box>
                  </Tooltip>
                </Group>
              </Group>

              <ScrollArea maw={{ base: 768, md: 1024, lg1: "100%" }} h={"100%"}>
                <Table
                  ref={earningsByServicesTableRef}
                  withRowBorders={true}
                  mt={"1rem"}
                  miw={1280}
                  withTableBorder
                  highlightOnHover
                  highlightOnHoverColor="var(--clr-gray-100)">
                  <Table.Thead>
                    <Table.Tr bg={"var(--clr-gray-100)"}>
                      <Table.Th>{t("date-of-service")}</Table.Th>
                      <Table.Th>{t("consultation-id")}</Table.Th>
                      <Table.Th>{t("service")}</Table.Th>
                      <Table.Th>{t("bill-status")}</Table.Th>

                      <Table.Th>
                        <Select
                          miw={200}
                          maw={275}
                          placeholder={t("doctor")}
                          data={doctors}
                          rightSection={
                            isDoctorsDataLoading ? <Loader size={16} /> : null
                          }
                          searchable
                          clearable
                          value={doctor}
                          onChange={(_, option) => {
                            setDoctor(option?.value);
                            setEarningsByServicesCurrentPage(1);
                          }}
                        />
                      </Table.Th>
                      <Table.Th>{t("report")}</Table.Th>
                      <Table.Th>{t("amount-paid-in")}</Table.Th>
                      <Table.Th>{t("doctor-earnings")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {renderLoadingErrorData({
                      isLoading: isEarningsByServicesLoading,
                      isError: isEarningsByServicesError,
                      isSuccess: isEarningsByServicesSuccess,
                      dataLength: earningsByServicesData?.newData?.length ?? 0,
                      emptyDataComponent: (
                        <TableEmptyRow
                          colSpan={earningsByServicesColumnCount}
                          message={t("empty-data")}
                        />
                      ),
                      errorComponent: (
                        <TableErrorRow
                          colSpan={earningsByServicesColumnCount}
                          message={t("encountered-error")}
                        />
                      ),
                      loadingComponent: (
                        <TableSkeletonRows
                          columns={earningsByServicesColumnCount}
                          skeletonProps={{
                            widths: [
                              "md",
                              "md",
                              "md",
                              "md",
                              "lg",
                              "md",
                              "md",
                              "md",
                            ],
                          }}
                        />
                      ),
                      dataComponent: earningsByServicesRows,
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
              <TablePagination
                paginationData={earningsByServicesData?.pagination || {}}
                paginatedRows={earningsByServicesData?.newData?.length || 0}
                onPageChange={setEarningsByServicesCurrentPage}
                isLoading={isEarningsByServicesLoading}
              />
            </Paper>
          </Grid.Col>
        </Grid>

        <Grid mt="3rem" gutter={"lg"}>
          <Grid.Col span={{ base: 12 }}>
            <Paper withBorder p="md" w={{ base: "fit-content", md: "100%" }}>
              <Group justify="space-between">
                <Title order={3} fz={17} fw={500}>
                  {t("payouts")}
                </Title>
              </Group>
              <Group mt="1rem">
                <Group>
                  <Text fz={14} fw={500}>
                    {t("payout-status")}:
                  </Text>
                  <Select
                    maw={150}
                    data={[
                      { label: t("all"), value: "all" },
                      { label: t("completed"), value: "completed" },
                      { label: t("pending"), value: "pending" },
                      { label: t("processing"), value: "processing" },
                      { label: t("failed"), value: "failed" },
                    ]}
                    value={payoutsStatusFilter?.value ?? null}
                    onChange={(_, option) => {
                      setPayoutsStatusFilter(option);
                      setPayoutsCurrentPage(1);
                    }}
                    clearable
                  />
                </Group>
              </Group>

              <ScrollArea maw={{ base: 768, md: 1024, lg1: "100%" }} h={"100%"}>
                <Table
                  ref={payoutsTableRef}
                  withRowBorders={true}
                  mt={"1rem"}
                  miw={1000}
                  withTableBorder
                  highlightOnHover
                  highlightOnHoverColor="var(--clr-gray-100)">
                  <Table.Thead>
                    <Table.Tr bg={"var(--clr-gray-100)"}>
                      <Table.Th>{t("date")}</Table.Th>
                      <Table.Th>{t("recipient")}</Table.Th>
                      <Table.Th>{t("amount")}</Table.Th>
                      <Table.Th>{t("type")}</Table.Th>
                      <Table.Th>{t("method")}</Table.Th>
                      <Table.Th>{t("status")}</Table.Th>
                      <Table.Th ta="right">{t("action")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>                  <Table.Tbody>
                    {renderLoadingErrorData({
                      isLoading: isPayoutsLoading,
                      isError: isPayoutsError,
                      isSuccess: isPayoutsSuccess,
                      dataLength: payoutsData?.newData?.length ?? 0,
                      emptyDataComponent: (
                        <TableEmptyRow
                          colSpan={payoutsColumnCount}
                          message={t("empty-data")}
                        />
                      ),
                      errorComponent: (
                        <TableErrorRow
                          colSpan={payoutsColumnCount}
                          message={t("encountered-error")}
                        />
                      ),
                      loadingComponent: (
                        <TableSkeletonRows
                          columns={payoutsColumnCount}
                          skeletonProps={{
                            widths: ["md", "lg", "md", "md", "md", "md"],
                          }}
                        />
                      ),
                      dataComponent: payoutsRows,
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
              <TablePagination
                paginationData={{
                  page: payoutsData?.page || payoutsCurrentPage,
                  pageSize: payoutsData?.pageSize || DEFAULT_PAGINATION.pageSize,
                  total: payoutsData?.total || 0,
                  pageCount: payoutsData?.pageCount || 1,
                }}
                paginatedRows={payoutsData?.newData?.length || 0}
                onPageChange={setPayoutsCurrentPage}
                isLoading={isPayoutsLoading}
              />
            </Paper>
          </Grid.Col>
        </Grid>

        <Grid mt="3rem" mb="8rem" gutter={"lg"}>
          <Grid.Col span={{ base: 12 }}>
            <Paper withBorder p="md" w={{ base: "fit-content", md: "100%" }}>
              <Group justify="space-between">
                <Title order={3} fz={17} fw={500}>
                  {t("earnings-by-staff")}
                </Title>
                <Group gap={"xs"}>
                  {/* <Button leftSection={<IconDownload size={16} />} disabled>
                    {t("export-csv")}
                  </Button> */}
                  <DateRangeSelect
                    dateRange={earningsByStaffDateRange}
                    setDateRange={newRange => {
                      setEarningsByStaffDateRange(newRange);
                      setEarningsByStaffCurrentPage(1); // Reset to first page when date range changes
                    }}
                    customDateRange={customEarningsByStaffDateRange}
                    setCustomDateRange={newRange => {
                      setCustomEarningsByStaffDateRange(newRange);
                    }}
                  />
                </Group>
              </Group>
              <TextInput
                leftSection={<IconSearch size={16} stroke={1.5} />}
                placeholder={t("search-for-staff")}
                mt={"0.5rem"}
                maw={300}
                value={searchForStaff}
                onChange={e => {
                  setSearchForStaff(e.target.value);
                  setEarningsByStaffCurrentPage(1);
                }}
              />

              <ScrollArea maw={{ base: 768, md: 1024, lg1: "100%" }} h={"100%"}>
                <Table
                  ref={earningsByStaffTableRef}
                  withRowBorders={true}
                  mt={"1rem"}
                  miw={1280}
                  withTableBorder
                  highlightOnHover
                  highlightOnHoverColor="var(--clr-gray-100)">
                  <Table.Thead>
                    <Table.Tr bg={"var(--clr-gray-100)"}>
                      <Table.Th>{t("staff")}</Table.Th>
                      <Table.Th>
                        <Group align="center" gap={"0.5rem"}>
                          {t("total-earnings")}
                          {/* <IconTriangleInvertedFilled size={12} /> */}
                        </Group>
                      </Table.Th>
                      <Table.Th>{t("current-commission-percent")}</Table.Th>

                      <Table.Th>{t("next-commission-percent")}</Table.Th>
                      <Table.Th>
                        {t("threshold-for-next-commission-percent")}
                      </Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {renderLoadingErrorData({
                      isLoading: isEarningsByStaffLoading,
                      isError: isEarningsByStaffError,
                      isSuccess: isEarningsByStaffSuccess,
                      dataLength: earningsByStaffData?.newData?.length ?? 0,
                      emptyDataComponent: (
                        <TableEmptyRow
                          colSpan={earningsByStaffColumnCount}
                          message={t("empty-data")}
                        />
                      ),
                      errorComponent: (
                        <TableErrorRow
                          colSpan={earningsByStaffColumnCount}
                          message={t("encountered-error")}
                        />
                      ),
                      loadingComponent: (
                        <TableSkeletonRows
                          columns={earningsByStaffColumnCount}
                          skeletonProps={{
                            widths: [
                              "md",
                              "md",
                              "md",
                              "md",
                              "lg",
                              "md",
                              "md",
                              "md",
                            ],
                          }}
                        />
                      ),
                      dataComponent: earningsByStaffRows,
                    })}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
              <TablePagination
                paginationData={earningsByStaffData?.pagination || {}}
                paginatedRows={earningsByStaffData?.newData?.length || 0}
                onPageChange={setEarningsByStaffCurrentPage}
                isLoading={isEarningsByStaffLoading}
              />
            </Paper>
          </Grid.Col>
        </Grid>
    </>
  );
};

export const Earnings = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === "referral-rewards"
    ? "referral-rewards"
    : "overview";

  return (
    <WithSidebar hasDashboardNavbar hasBackground={false}>
      <WithDashboardNavbar userRole={ROLES.ADMIN}>
        <TabsRoot
          value={activeTab}
          onValueChange={value => {
            const params = new URLSearchParams(searchParams);
            if (value === "overview") params.delete("tab");
            else params.set("tab", value);
            setSearchParams(params, { replace: true });
          }}>
          <TabsList
            ariaLabel={t("earnings")}
            comboboxItems={[
              { value: "overview", label: t("overview") },
              { value: "referral-rewards", label: t("referral-rewards.title") },
            ]}
          />
          <TabsContent value="overview"><EarningsOverview /></TabsContent>
          <TabsContent value="referral-rewards"><ReferralRewardsTab /></TabsContent>
        </TabsRoot>
      </WithDashboardNavbar>
    </WithSidebar>
  );
};
